import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { listWidgets, widgetName, widgetRegistry } from '@/widgets/_registry';
import { widgetText } from '@/widgets/_sdk/format';
import type { AnyWidget, WidgetCategory } from '@/widgets/_sdk/types';

/** The widget catalog and the widget editor (PRD VS-041, VS-042: configurable from the UI). */

export function WidgetCatalog({
  open,
  onOpenChange,
  onPick,
  registry = widgetRegistry,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (type: string) => void;
  registry?: ReadonlyMap<string, AnyWidget>;
}) {
  const { t } = useTranslation(['dashboards', 'common']);
  const groups = new Map<WidgetCategory, AnyWidget[]>();
  for (const w of listWidgets(registry))
    groups.set(w.category, [...(groups.get(w.category) ?? []), w]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('dashboards:editor.cancel')}>
        <DialogTitle>{t('dashboards:catalog.title')}</DialogTitle>
        <DialogDescription>{t('dashboards:catalog.description')}</DialogDescription>
        {[...groups.entries()].map(([category, widgets]) => (
          <section key={category} className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t(`dashboards:catalog.categories.${category}`)}
            </h3>
            <ul className="grid gap-2 sm:grid-cols-2">
              {widgets.map((w) => (
                <li key={w.type}>
                  <button
                    type="button"
                    onClick={() => onPick(w.type)}
                    className="flex h-full w-full flex-col items-start gap-1 rounded-md border p-3 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    <span className="text-sm font-medium">{widgetName(w.type)}</span>
                    <span className="text-xs text-muted-foreground">
                      {widgetText(w.type)('description', { defaultValue: '' })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export interface EditorTarget {
  type: string;
  props: Record<string, unknown>;
  /** Absent for a widget being added. */
  id?: string | undefined;
}

export function WidgetEditor({
  target,
  onClose,
  onSave,
  registry = widgetRegistry,
}: {
  target: EditorTarget | null;
  onClose: () => void;
  onSave: (target: EditorTarget, props: Record<string, unknown>) => void;
  registry?: ReadonlyMap<string, AnyWidget>;
}) {
  const { t } = useTranslation('dashboards');
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  useEffect(() => setDraft(target?.props ?? {}), [target]);

  const widget = target ? registry.get(target.type) : undefined;
  const parsed = widget?.propsSchema.safeParse(draft);
  const name = target ? widgetName(target.type) : '';
  const Editor = widget?.Editor;

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel={t('editor.cancel')}>
        <DialogTitle>
          {target?.id ? t('editor.titleEdit', { name }) : t('editor.titleNew', { name })}
        </DialogTitle>
        <DialogDescription>{t('editor.description')}</DialogDescription>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (target && parsed?.success) onSave(target, parsed.data as Record<string, unknown>);
          }}
        >
          {Editor && (
            <Suspense
              fallback={
                <output className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Spinner />
                  {t('editor.loading')}
                </output>
              }
            >
              <Editor props={draft} onChange={(next) => setDraft({ ...next })} />
            </Suspense>
          )}
          {parsed && !parsed.success && (
            <p className="text-xs text-muted-foreground">{t('editor.invalid')}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('editor.cancel')}
            </Button>
            <Button type="submit" disabled={!parsed?.success}>
              {target?.id ? t('editor.save') : t('editor.add')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
